/** Production Canvas — Node Registry
 * (VISTOERA_PRODUCTION_CANVAS_V1_FOUNDATION_AND_INTERACTIVE_SHELL spec §5).
 *
 * Single Source of Truth for every creatable node type: label, icon,
 * category, which "renderer family" draws it (`entity` / `action` /
 * `scene`), and which node types are offered by the Connection-to-Create
 * popup (spec §6D) when dragging a connection from this type's output
 * into empty canvas space. Every UI surface (Left Library, Context Menu,
 * Quick Add, Connection-to-Create) reads from this ONE map — none of
 * them hard-code a node list of their own, so adding a node type here is
 * the only change needed to make it creatable everywhere (spec §5 "avoid
 * giant switch statements scattered throughout the frontend"). */

import {
  AppstoreOutlined,
  AudioOutlined,
  BarChartOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClusterOutlined,
  CustomerServiceOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileImageOutlined,
  FileTextOutlined,
  IdcardOutlined,
  LinkOutlined,
  MessageOutlined,
  PictureOutlined,
  PlaySquareOutlined,
  RobotOutlined,
  RocketOutlined,
  ScissorOutlined,
  SkinOutlined,
  SoundOutlined,
  StockOutlined,
  NodeIndexOutlined,
  StarOutlined,
  ThunderboltOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { useEffect, useState, type ReactNode } from 'react'
import type { CanvasNodeCategory } from './types'
import { FREE_RATIO } from './imageGenerationRatio'
import {
  FALLBACK_ICON_KEY,
  type NodeIconKey,
} from './nodeIconCatalog'
import {
  getAssignedIconRef,
  getCanvasIconAssignmentRevision,
  mergeCanvasIconAssignments,
  resolveCanvasNodeIcon,
  setCanvasIconAssignments,
  subscribeCanvasIconAssignments,
} from './canvasNodeIconAssignment'

/** VISTOERA_GLOBAL_FUNCTION_ICON_ASSET_REGISTRY... V2 — Admin / catalog
 * icon_ref assignments are module-level so Canvas surfaces share one map
 * (no per-node fetch). Canonical assignment source is Node Catalog
 * `icon_ref`; this map is the in-session projection.
 * A revision counter + subscribe hook forces React re-render when the
 * map arrives/changes (LeftLibraryPanel previously memoized only on
 * toolbar order and never saw async override loads). */
let _iconOverrides: Record<string, string> = {}
let _iconRevision = 0
const _iconListeners = new Set<() => void>()

function _bumpIconRevision(): void {
  _iconRevision += 1
  _iconListeners.forEach((l) => l())
}

/**
 * Replace session icon assignment map (node_type → icon_ref).
 * Accepts builtin keys and `custom:<uuid>` refs. Does NOT decide
 * occupancy — Platform catalog is the occupancy source of truth.
 */
export function setNodeIconOverrides(overrides: Record<string, string>): void {
  const next: Record<string, string> = {}
  for (const [nodeType, key] of Object.entries(overrides || {})) {
    if (!key || typeof key !== 'string') continue
    next[nodeType] = key
  }
  _iconOverrides = next
  setCanvasIconAssignments(next)
  _bumpIconRevision()
}

/** Merge without dropping unrelated assignments (catalog partial updates). */
export function mergeNodeIconOverrides(partial: Record<string, string>): void {
  const next = { ..._iconOverrides }
  for (const [nodeType, key] of Object.entries(partial || {})) {
    if (!key || typeof key !== 'string') continue
    next[nodeType] = key
  }
  _iconOverrides = next
  mergeCanvasIconAssignments(partial || {})
  _bumpIconRevision()
}

export function getNodeIconOverrides(): Record<string, string> {
  return _iconOverrides
}

export function getNodeIconRevision(): number {
  return _iconRevision
}

/** Subscribe Canvas / Admin surfaces so icon_key changes re-render. */
export function useNodeIconRevision(): number {
  const [rev, setRev] = useState(_iconRevision + getCanvasIconAssignmentRevision())
  useEffect(() => {
    const listener = () => setRev(getNodeIconRevision() + getCanvasIconAssignmentRevision())
    _iconListeners.add(listener)
    const unsubAssign = subscribeCanvasIconAssignments(listener)
    // Sync in case overrides landed between first render and effect.
    listener()
    return () => {
      _iconListeners.delete(listener)
      unsubAssign()
    }
  }, [])
  return rev
}

/** Hook form — components that render icons must use this (or call
 * `useNodeIconRevision` + `getNodeDefinition`) so Admin overrides paint. */
export function useNodeDefinition(nodeType: string): NodeDefinition {
  useNodeIconRevision()
  return getNodeDefinition(nodeType)
}

export function resolveIconByKey(key: string | undefined | null): ReactNode {
  return resolveCanvasNodeIcon(key)
}

/** `media` (spec §5 — Node Detail Shell / Image Asset UI V2) is for
 * media-first "asset" nodes whose compact card IS the media preview
 * (image fills the card, near-zero chrome) rather than a text/icon
 * card like every other `entity` node. Only `image_asset` uses it this
 * round; Video/Audio would be natural next adopters (spec §21) once
 * they get their own `NodeDetailRenderer`.
 *
 * `generator` (VISTOERA_PRODUCTION_CANVAS_IMAGE_TO_IMAGE_GENERATION_FLOW_V1
 * §8/§9, redesigned as a Media Card by
 * VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2 §9–§28)
 * is "生成图片" — compact media card once it has a result (default
 * collapsed, click to expand its attached-below Generation Inspector),
 * a full inline composer while still empty/generating. One card = one
 * result (spec §9) — a multi-candidate run splits into sibling nodes,
 * never one node accumulating an unbounded generation history.
 *
 * `video_generator` (spec §29–§37) is "生成视频" — same media-card
 * mental model, split into a TOP Media Area (video / empty-state, the
 * ONLY place its connection handles live) and a BOTTOM Generation
 * Inspector that's always visible while empty (there is nothing to
 * collapse to yet). UI/interaction foundation only this round — no real
 * video model wired in (spec §29/§49).
 *
 * `text_asset` (VISTOERA_PRODUCTION_CANVAS_TYPED_DATAFLOW_TEXT_ASSET_LINEAGE_HIGHLIGHT_AND_TOOL_REORDER_P0
 * §9–§11) is "文字 / 文本素材" — TEXT elevated to a first-class Source/
 * Asset material, own compact card + own dedicated edit affordance
 * (`TextAssetNode.tsx`), deliberately its OWN family rather than reusing
 * `entity` (which has no editing UI at all) or `media` (which is
 * image-specific: aspect-ratio box sizing, lightbox, etc. make no sense
 * for text).
 *
 * `audio_asset` (VistoeraTASK-00115) is "音频资产" — an EXISTING audio
 * asset (speech/music/sfx/ambience/recording/reference/mixdown) bound
 * via the canonical Audio Asset Picker. Own family (`AudioAssetNode.tsx`)
 * rather than reusing `media`/`video_media` (both size a visual box to
 * pixel dimensions, meaningless for audio) — renders a native
 * `<audio controls preload="metadata">` player (no autoplay, no eager
 * binary fetch) plus the same header/hover-toolbar chrome as Image/Video
 * Asset. Never `audio_generator` (that stays `generate_voice`'s own
 * family — Asset vs Generation is a hard product distinction here, see
 * task §3/§27-29) and never conflated with Voice Template identity. */
/** `project_start` (VISTOERA_CANVAS_PROJECT_START_CREATIVE_BRIEF_AND_
 * IDEATION_BRANCHING_NODE_V1 §2-§4) is "项目发起" — the Creative Brief
 * SOURCE node (intent/duration/content-goal/assets/skill/provider +
 * "生成创意方向"). Its own family because it is NOT an AI Provider
 * execution card like `generator`/`tryon_generator` (§3 "它本身不是 AI
 * Provider 执行节点" — Provider here belongs to the downstream Creative
 * Ideation run, not to this node's own identity) and its content
 * (8 distinct fields + collapsible "更多创作要求") doesn't fit any
 * existing compact-card family.
 *
 * `creative_candidate` (§31/§32) is one AI-generated creative direction
 * — title/premise/hook/tone/duration/scene-estimate + select/expand/
 * discard actions. Deliberately its own family, never `entity` (no
 * select/expand actions) or `generator` (no media/result-URL model —
 * "一个候选一个结果" here means a candidate's OWN text fields, not a
 * generated image/video). BOTH `project_start` and `creative_candidate`
 * (and their families below) are kept COMPLETELY UNCHANGED by
 * VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_GENERATION_
 * NODE_V2 — they are the LEGACY read-time-compat rendering path for
 * Canvas JSON persisted before this round (V2 spec §2/§15 "已存在
 * Preview Canvas 中的旧 project_start 节点必须还能加载...优先 read-time
 * normalization，避免破坏性 migration"); see `generate_creative` below
 * for the new canonical node every "Add Node" surface now creates.
 *
 * `generate_creative` (V2 §1/§2/§6) is "生成创意" — the ONE unified
 * "生成器 + 当前结果载体" node that collapses V1's two-node model into a
 * single card: Brief/Prompt + Structured Context Assets + Skill +
 * Provider + Generate/Regenerate (always exactly 1 result, replacing
 * the node's own `fields.currentResult`, old version stays queryable
 * via `CreativeIdeationRun` history — never a new sibling node) +
 * Diverge (three-way fork → exactly 3 NEW, fully independent
 * `generate_creative` nodes). New canonical/internal AND user-visible
 * node type going forward (V2 §2's own "评估是否引入 canonical node
 * type" — chosen over reusing `project_start`'s internal type so this
 * and every future `generate_video_script`/`generate_storyboard`/...
 * sibling share one obvious naming convention, and so the legacy
 * dual-node rendering path above never has to branch on "is this OLD or
 * NEW project_start data" inside the same component).
 *
 * `script_generation` (VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_
 * CREATIVE_ASSET_CONTEXT_LINKING_V1 §1/§2) is "生成剧本" — the next
 * stage after `generate_creative`: receives a dragged-in Creative Idea
 * as its Primary Creative Context (never a `project_start`/`creative_
 * candidate`-style Brief of its own — the Creative Idea IS its brief),
 * plus Supporting Structured Context Assets inherited from that idea,
 * script settings, Skill + Provider, and the same Generate/Diverge
 * generation-node grammar as `generate_creative` above (one node = one
 * generator + current result carrier, Diverge → 3 independent sibling
 * nodes). Own family because its content (Creative Context card +
 * Supporting Assets + script settings + structured `ScriptArtifact`
 * result) doesn't fit `generator`'s media-result model or `generate_
 * creative`'s Brief-first layout. */
export type NodeRenderFamily = 'entity' | 'action' | 'scene' | 'media' | 'video_media' | 'audio_asset' | 'generator' | 'video_generator' | 'voice_generator' | 'audio_generator' | 'text_asset' | 'motion_asset' | 'motion_processor' | 'tryon_generator' | 'apparel_video_generator' | 'project_start' | 'creative_candidate' | 'generate_creative' | 'script_generation' | 'storyboard_generation' | 'text_generator'

export interface NodeDefinition {
  nodeType: string
  label: string
  category: CanvasNodeCategory
  family: NodeRenderFamily
  /** Canonical icon_key (spec §8) — the ONE stable id every surface
   * resolves through `NODE_ICON_CATALOG`. `icon` (below) is always
   * `resolveIconByKey(iconKey)`'s un-overridden default; call
   * `getNodeDefinition()` (never read `NODE_REGISTRY` directly) to get
   * the current, possibly admin-overridden icon. */
  iconKey: NodeIconKey | string
  icon: ReactNode
  color: string
  description: string
  /** Node types offered by Connection-to-Create (spec §6D) when dragging
   * a connection out of this type's source handle into empty space. */
  suggestedNext: string[]
  /** Default `data.fields` seed when a new node of this type is created. */
  defaultFields?: Record<string, unknown>
}

// VistoeraTASK-00115 — Add Node taxonomy rename: "内容 / 上下文" → "资产"
// and "生产" → "生成". Purely a category display-label change — the
// underlying `CanvasNodeCategory` keys ('content' / 'production') are
// UNCHANGED (no data migration, no persisted-graph impact) and every
// other real "Structured Context" / "Content Production Center" /
// "Production environment" concept elsewhere in the product is
// deliberately left untouched (task §66-71). Actual translated text
// lives in `canvas.json` (`categories.<key>`) — these are the
// code-level fallback only (`useCanvasNodeRegistryI18n().categoryLabel`
// prefers i18n first).
export const CATEGORY_LABELS: Record<CanvasNodeCategory, string> = {
  content: '资产',
  production: '生成',
  intelligence: '智能 / 自动化',
  output: '输出',
}

export const CATEGORY_ORDER: CanvasNodeCategory[] = ['content', 'production', 'intelligence', 'output']

const CONTENT = 'content' as const
const PRODUCTION = 'production' as const
const INTELLIGENCE = 'intelligence' as const
const OUTPUT = 'output' as const

export const NODE_DEFINITIONS: NodeDefinition[] = [
  // ---------------------------------------------------------- CONTENT / CONTEXT
  {
    nodeType: 'project_start',
    label: '项目发起',
    category: CONTENT,
    family: 'project_start',
    iconKey: 'rocket',
    icon: <RocketOutlined />,
    color: '#8b7bff',
    description: '定义创作意图、时长、内容目的和相关资产 — 整个 AI 内容生产流程的 Creative Brief 起点。',
    suggestedNext: ['creative_candidate'],
    // `supports_provider_selection = NO` (task spec section 3/19) — this
    // node itself is a Creative Brief SOURCE, never an AI Provider
    // execution card; Provider selection lives on the downstream
    // Creative Ideation run fields below (`providerSelectionMode` etc.),
    // not on this node's own identity.
    defaultFields: {
      userIntent: '',
      contentGoal: null,
      contentGoalSource: 'UNKNOWN',
      targetDurationSeconds: 30,
      durationSource: 'DEFAULT_UI_VALUE',
      assets: [],
      mustInclude: [],
      mustAvoid: [],
      audience: null,
      platform: null,
      tone: null,
      references: [],
      skillSelectionSource: 'AUTO',
      skillKey: null,
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
      candidateCount: 3,
      moreRequirementsExpanded: false,
      activeRunId: null,
      runStatus: null,
      runErrorMessage: null,
    },
  },
  {
    nodeType: 'creative_candidate',
    label: '创意候选',
    category: PRODUCTION,
    family: 'creative_candidate',
    iconKey: 'sparkles',
    icon: <StarOutlined />,
    color: '#B98CE0',
    description: '一个 AI 生成的创意方向候选 — 可选择、发散出更多子方向，或淘汰。',
    suggestedNext: ['creative_candidate'],
    defaultFields: {
      candidateId: null,
      title: '',
      premise: '',
      hook: '',
      tone: '',
      storyDirection: '',
      productRole: null,
      characterRole: null,
      estimatedDuration: null,
      tags: [],
      rationale: '',
      selected: false,
      rejected: false,
      stale: false,
      rootBriefNodeId: null,
      parentCandidateNodeId: null,
      generationDepth: 0,
      sourceRunId: null,
      rootRunId: null,
      briefFingerprintAtRun: null,
      // "发散" (task spec §37-§39) is its own AI run scoped to THIS
      // candidate node (backend's `canvas_node_id == candidate_node_id`
      // for an EXPAND run) — tracked separately from the Ideation run
      // that CREATED this candidate (`sourceRunId` above).
      activeExpandRunId: null,
      expandRunStatus: null,
      expandRunErrorMessage: null,
    },
  },
  {
    nodeType: 'script_generation',
    label: '生成剧本',
    category: PRODUCTION,
    family: 'script_generation',
    iconKey: 'screenplay',
    icon: <FileDoneOutlined />,
    color: '#5B9BFF',
    description: '基于拖入的创意结果生成结构化剧本 — 角色 / 场景 / 台词 / 时长，为下一阶段“生成分镜”准备上下文。',
    suggestedNext: ['storyboard_generation'],
    defaultFields: {
      // Primary Creative Context (spec §5) — populated only once a
      // `generate_creative` node's `fields.currentResult` (or a legacy
      // `creative_candidate` node's own result) is dropped here (see
      // `ScriptCreativeContextField` in `scriptGenerationCanvas.ts`);
      // `null` renders the "将创意拖到这里生成剧本" drop target (spec §7).
      creativeContext: null,
      // Supporting Assets (spec §5/§6) — REFERENCES only
      // (`ScriptSupportingAssetField[]`), auto-seeded from the dropped
      // Creative Idea's own linked assets but independently editable.
      supportingAssets: [],
      contentType: '广告剧本',
      scriptStructure: null,
      generationRequirements: [],
      additionalRequirements: '',
      targetDurationSeconds: 30,
      skillSelectionSource: 'AUTO',
      skillKey: null,
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
      // Current Script Result (spec §16) + its own Generation History
      // (spec §12) + the fingerprint of every input that produced it
      // (spec §9's `needsRegeneration` derivation — see
      // `computeNeedsRegeneration`, never stored as a boolean itself).
      scriptResult: null,
      scriptResultVersion: 0,
      scriptArtifactId: null,
      generationHistory: [],
      paramsFingerprintAtGeneration: null,
      activeRunId: null,
      runStatus: null,
      runErrorMessage: null,
      activeDivergeRunId: null,
      divergeRunStatus: null,
      divergeRunErrorMessage: null,
      divergenceGroupId: null,
      divergenceSiblingIndex: null,
      divergenceOriginNodeId: null,
    },
  },
  {
    // VISTOERA_CANVAS_STORYBOARD_GENERATION_NODE_SCRIPT_CONTEXT_AND_SHOT_
    // ARTIFACT_V1 — Wide Generation Card: Script Context → ShotArtifact[].
    nodeType: 'storyboard_generation',
    label: '生成分镜',
    category: PRODUCTION,
    family: 'storyboard_generation',
    iconKey: 'shot',
    icon: <ClusterOutlined />,
    color: '#3DB8A8',
    description: '基于剧本生成结构化分镜，并在本卡完成 Shot 视频编排、Rough Cut 预览与最终合并。',
    suggestedNext: ['generate_video'],
    defaultFields: {
      scriptContext: null,
      supportingAssets: [],
      shotCountMode: 'AUTO',
      shotCount: null,
      durationMode: 'AUTO',
      totalDurationSec: null,
      styleReference: 'AUTO',
      cameraLanguage: 'AUTO',
      aspectRatio: '16:9',
      productionFormat: { aspect_ratio: '16:9', resolution: '1080p', version: 1 },
      productionFormatAtGeneration: null,
      additionalInstructions: '',
      shotListViewMode: 'story',
      skillSelectionSource: 'AUTO',
      skillKey: null,
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
      storyboardResult: null,
      storyboardResultVersion: 0,
      storyboardArtifactId: null,
      generationHistory: [],
      paramsFingerprintAtGeneration: null,
      activeRunId: null,
      runStatus: null,
      runErrorMessage: null,
      activeDivergeRunId: null,
      divergeRunStatus: null,
      divergeRunErrorMessage: null,
      divergenceGroupId: null,
      divergenceSiblingIndex: null,
      divergenceOriginNodeId: null,
      // Unified video orchestration (was standalone video_production).
      shotBindings: {},
      assemblyProvider: 'STANDARD_FFMPEG',
      assemblySkill: 'STANDARD_VIDEO_ASSEMBLY',
      finalVideoArtifact: null,
      activeAssemblyId: null,
      assemblyRunStatus: null,
      assemblyErrorMessage: null,
      supersededStoryboardSnapshot: null,
      storyboardRegenerateActive: false,
      generationInputSnapshot: null,
    },
  },
  {
    nodeType: 'virtual_ip',
    label: '虚拟 IP',
    category: CONTENT,
    family: 'entity',
    iconKey: 'virtual-ip',
    icon: <IdcardOutlined />,
    color: '#8b7bff',
    description: '引用一个虚拟 IP 人设作为本项目的内容主理人。',
    suggestedNext: ['generate_production_plan', 'character'],
  },
  {
    nodeType: 'reference_content',
    label: '参考内容',
    category: CONTENT,
    family: 'entity',
    iconKey: 'reference-capture',
    icon: <LinkOutlined />,
    color: '#5B9BFF',
    description: '一段被分析过的竞品/参考内容。',
    suggestedNext: ['generate_production_plan', 'analyze_content'],
  },
  {
    nodeType: 'character',
    label: '人物角色',
    category: CONTENT,
    family: 'entity',
    iconKey: 'character',
    icon: <UserOutlined />,
    color: '#5B9BFF',
    description: '出现在场景中的一个角色（可被多条台词复用）。',
    suggestedNext: ['dialogue', 'reference_content'],
    defaultFields: { label: '新角色' },
  },
  {
    nodeType: 'dialogue',
    label: '台词 / 文案',
    category: CONTENT,
    family: 'entity',
    iconKey: 'script',
    icon: <MessageOutlined />,
    color: '#5EC9A6',
    description: '一句角色台词或文案片段。',
    suggestedNext: ['voice', 'keyframe'],
    defaultFields: { label: '新台词', text: '' },
  },
  {
    nodeType: 'image_asset',
    label: '图片 / 素材',
    category: CONTENT,
    family: 'media',
    iconKey: 'image-asset',
    icon: <PictureOutlined />,
    color: '#E8A94B',
    description: '一张已有的图片素材。',
    suggestedNext: ['generate_video', 'generate_keyframe'],
  },
  {
    nodeType: 'video_asset',
    label: '视频 / 素材',
    category: CONTENT,
    family: 'video_media',
    iconKey: 'video-asset',
    icon: <VideoCameraOutlined />,
    color: '#E8A94B',
    description: '一段已有或已生成的视频素材。',
    suggestedNext: ['motion_reconstruction', 'edit_composite'],
  },
  {
    // VistoeraTASK-00115 — canonical first-class "音频资产" Asset Node,
    // same tier as image_asset/video_asset/text_asset/virtual_ip. Binds a
    // canonical audio asset id via the existing Audio Asset Picker
    // (`components/AudioAssetPicker`, TASK-00087) — never a second audio
    // library/table. Deliberately NOT `audio_generator` (that stays
    // `generate_voice`'s own family) and deliberately distinct from Voice
    // Template (`generate_voice_template` / `voice_template` picker
    // category) — an Audio Asset is playable content (speech/music/sfx/
    // ambience/recording/reference/mixdown), never a voice identity.
    nodeType: 'audio_asset',
    label: '音频资产',
    category: CONTENT,
    family: 'audio_asset',
    iconKey: 'audio-stock',
    icon: <StockOutlined />,
    color: '#E8A94B',
    description: '一段已有的音频素材（上传/生成语音/音乐/音效/环境音/录音/参考音频等），可作为下游节点的结构化音频输入。',
    suggestedNext: [],
  },
  {
    nodeType: 'text_asset',
    label: '文字 / 文本素材',
    category: CONTENT,
    family: 'text_asset',
    iconKey: 'text',
    icon: <FileTextOutlined />,
    color: '#5EC9A6',
    description: '一段可编辑的文字素材（人设描述、场景设定……），作为其他生成节点的输入上下文，而非生成指令本身。',
    suggestedNext: ['generate_image', 'generate_video'],
    defaultFields: { text: '' },
  },
  {
    nodeType: 'motion_asset',
    label: '动作素材',
    category: CONTENT,
    family: 'motion_asset',
    iconKey: 'motion-asset',
    icon: <NodeIndexOutlined />,
    color: '#5EC9A6',
    description: '一段可预览、可连接的动作素材（2D/3D 骨架）。',
    suggestedNext: [],
    defaultFields: { motionType: 'MOTION_2D', label: '动作素材' },
  },
  {
    nodeType: 'voice',
    label: '配音 / 声音',
    category: CONTENT,
    family: 'entity',
    iconKey: 'audio',
    icon: <SoundOutlined />,
    color: '#5EC9A6',
    description: '一句台词对应的配音音频。',
    suggestedNext: ['generate_video'],
    defaultFields: { label: '新配音' },
  },
  {
    nodeType: 'music_sfx',
    label: '音乐 / 音效',
    category: CONTENT,
    family: 'entity',
    iconKey: 'music-sfx',
    icon: <CustomerServiceOutlined />,
    color: '#5EC9A6',
    description: '背景音乐或音效素材。',
    suggestedNext: ['edit_composite'],
  },
  {
    nodeType: 'keyframe',
    label: '关键帧',
    category: CONTENT,
    family: 'entity',
    iconKey: 'reference-media',
    icon: <PictureOutlined />,
    color: '#E8A94B',
    description: '一帧用于指导生成视频的关键画面。',
    suggestedNext: ['generate_video'],
    defaultFields: { label: '新关键帧' },
  },
  {
    nodeType: 'text_note',
    label: '文本说明',
    category: CONTENT,
    family: 'entity',
    iconKey: 'text',
    icon: <FileTextOutlined />,
    color: '#94a3b8',
    description: '一段自由文本备注，不与任何业务实体绑定。',
    suggestedNext: [],
    defaultFields: { label: '备注', text: '' },
  },

  // ---------------------------------------------------------------- PRODUCTION
  {
    nodeType: 'scene',
    label: 'Scene / Shot 容器',
    category: PRODUCTION,
    family: 'scene',
    iconKey: 'shot',
    icon: <ClusterOutlined />,
    color: '#8b7bff',
    description: '一个场景/分镜容器，可容纳任意数量的人物、台词、配音、关键帧与生成的视频片段。',
    suggestedNext: ['character', 'generate_video'],
    defaultFields: { label: '新场景' },
  },
  {
    nodeType: 'generate_text',
    label: '生成文本',
    category: PRODUCTION,
    family: 'text_generator',
    iconKey: 'ai-chat',
    icon: <MessageOutlined />,
    color: '#F0637C',
    description: '基于输入图片生成可选语言与长度的文本结果；补充要求可选，零配置即可开始。',
    suggestedNext: ['dialogue', 'text_asset'],
    defaultFields: {
      inputAssets: [],
      language: 'zh-CN',
      contentLength: 'AUTO',
      outputFormat: 'plain',
      additionalInstruction: '',
      providerOptionId: null,
      providerSelectionMode: 'DEFAULT',
      skillKey: null,
      skillVersion: 'v1',
      skillSelectionSource: 'AUTO',
      preferredSkillKey: null,
      currentResult: null,
      generationHistory: [],
      runStatus: null,
      activeRunId: null,
      errorMessage: null,
      errorCode: null,
      needsRegeneration: false,
      collapsed: false,
      generatedFromFingerprint: null,
    },
  },
  {
    // VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_
    // GENERATION_NODE_V2 §1/§2/§6 — the unified "生成创意" node. See the
    // `NodeRenderFamily` doc comment above for why this is a NEW
    // canonical type rather than reusing `project_start`'s.
    nodeType: 'generate_creative',
    label: '生成创意',
    category: PRODUCTION,
    family: 'generate_creative',
    // Lightbulb — graphic asset reuse of `ai-deep-analysis` (icon_key ≠
    // function identity per `nodeIconCatalog.tsx`'s own header); no
    // dedicated "creative idea" glyph exists in the approved catalog and
    // a lightbulb fits "生成创意" better than `project_start`'s rocket or
    // `creative_candidate`'s star.
    iconKey: 'ai-deep-analysis',
    icon: <BulbOutlined />,
    color: '#8b7bff',
    description: '一句创作意图即可生成一个创意方向；可重新生成，也可一次发散出 3 个不同方向的新创意节点。',
    // VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
    // LINKING_V1 §1 — next stage after "生成创意" is "生成剧本" (the
    // Creative Idea this node holds is dragged into it, spec §3).
    suggestedNext: ['script_generation'],
    // `supports_provider_selection` for THIS node's own identity stays
    // NO (V1 §3's rule, unchanged) — Provider belongs to the Creative
    // Ideation run this node dispatches, not to the node type itself.
    defaultFields: {
      userIntent: '',
      contentGoal: null,
      contentGoalSource: 'UNKNOWN',
      targetDurationSeconds: 30,
      durationSource: 'DEFAULT_UI_VALUE',
      assets: [],
      mustInclude: [],
      mustAvoid: [],
      audience: null,
      platform: null,
      tone: null,
      references: [],
      skillSelectionSource: 'AUTO',
      skillKey: null,
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
      moreRequirementsExpanded: false,
      // V2 §1/§6 — the node's OWN current generation result. `null`
      // until the first successful Generate; overwritten (never
      // appended-beside) by every later Regenerate/Diverge-inheritance.
      currentResult: null,
      // Plain Generate/Regenerate run bookkeeping.
      activeRunId: null,
      runStatus: null,
      runErrorMessage: null,
      // Diverge run bookkeeping — deliberately separate from the plain
      // Generate fields above so a Diverge in flight never blocks (or is
      // blocked by) a plain Regenerate click, and vice versa.
      activeDivergeRunId: null,
      divergeRunStatus: null,
      divergeRunErrorMessage: null,
      // Diverge/branch lineage (V2 §5/§19 — reuses the EXACT same
      // concepts V1's `creative_candidate` used, just renamed to this
      // node's own vocabulary: this node's OWN parent, not a "root brief
      // node" — every `generate_creative` node IS its own brief owner).
      parentNodeId: null,
      rootRunId: null,
      generationDepth: 0,
    },
  },
  {
    nodeType: 'generate_image',
    label: '生成图片',
    category: PRODUCTION,
    family: 'generator',
    // Was `<ThunderboltOutlined />` — identical to `generate_video`'s
    // icon (VISTOERA_CANONICAL_NODE_REGISTRY... §6/§31 "Generate Image
    // = lightning, Generate Video = lightning，这种无区分状态" — fixed).
    iconKey: 'image-generation',
    icon: <FileImageOutlined />,
    color: '#F0637C',
    description: '调用图片生成能力。',
    suggestedNext: ['image_asset'],
    // No `mode`/FAST-STANDARD field (spec §15/§24 "不要做文生图/图生图模式
    // 切换器") — `operation` (CREATE_NEW vs IMAGE_TO_IMAGE) is derived
    // fresh every render from whether a reference edge exists, never a
    // persisted/user-facing toggle.
    defaultFields: {
      prompt: '',
      // VistoeraTASK-00498 — "自由比例（由提示词决定）" (no aspect-ratio constraint).
      ratio: FREE_RATIO,
      quality: 'STANDARD',
      candidateCount: 1,
      strength: 0.75,
      status: 'READY',
    },
  },
  {
    nodeType: 'generate_tryon',
    label: '虚拟换装',
    category: PRODUCTION,
    family: 'tryon_generator',
    iconKey: 'virtual-tryon',
    icon: <SkinOutlined />,
    color: '#E87B5B',
    description: '人物图片 + 服装图片 → 虚拟换装结果。',
    suggestedNext: ['image_asset', 'generate_video'],
    defaultFields: {
      outfitScope: 'FULL_LOOK_AUTO',
      outfitScopeSource: 'DEFAULT',
      category: 'dress',
      quality: 'balanced',
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
      skillSelectionSource: 'AUTO',
      skillKey: null,
      prompt: '',
      status: 'WAITING',
      jobId: null,
      generatedOutputId: null,
      outputAssetId: null,
      previousGeneratedOutputId: null,
      previousOutputAssetId: null,
      generationHistory: [],
      personAssetId: null,
      garmentAssetId: null,
      personLabel: null,
      garmentLabel: null,
      outputWidth: null,
      outputHeight: null,
      errorMessage: null,
      needsRegeneration: false,
    },
  },
  // VistoeraTASK-00161 — 服装视频换装 (Kling clothing_dupe). Deliberately
  // its own node/capability, never merged into `generate_tryon` above —
  // this replicates a REFERENCE VIDEO's pacing/camera/pose with the
  // tenant's real product (+ optional model/BGM), not an image garment
  // swap. See `app.domain.workers.WorkerCapability.
  // APPAREL_VIDEO_REPLICATION`'s docstring for the full semantic split.
  {
    nodeType: 'apparel_video_replication',
    label: '服装视频换装',
    category: PRODUCTION,
    family: 'apparel_video_generator',
    iconKey: 'apparel-video-replication',
    icon: <PlaySquareOutlined />,
    color: '#3E9C7A',
    description: '参考视频 + 商品图片 + 商品描述 → 电商服装视频复刻。',
    suggestedNext: ['video_asset'],
    defaultFields: {
      sourceVideoAssetId: null,
      sourceVideoLabel: null,
      productImages: [],
      modelImages: [],
      bgmAssetId: null,
      bgmLabel: null,
      productDescription: '',
      resolution: '720p',
      aspectRatio: '9:16',
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
      status: 'WAITING',
      jobId: null,
      outputAssetId: null,
      previousOutputAssetId: null,
      generationHistory: [],
      errorMessage: null,
      needsRegeneration: false,
    },
  },
  {
    nodeType: 'generate_voice_template',
    label: '声音模板生成',
    category: PRODUCTION,
    family: 'voice_generator',
    iconKey: 'waveform',
    icon: <SoundOutlined />,
    color: '#B98CE0',
    description: '创建可复用的 Voice Template（Voice Identity），供后续文本转语音节点引用。',
    suggestedNext: ['generate_voice'],
    defaultFields: {
      sourceType: 'AI_GENERATED',
      voiceDescription: '',
      previewText: '',
      templateName: '',
      referenceAssetId: null,
      referenceFilename: null,
      referenceDurationMs: null,
      voiceRightsConfirmed: false,
      status: 'READY',
      templateId: null,
      previewAudioAssetId: null,
      providerVoiceId: null,
      provider: null,
      errorMessage: null,
    },
  },
  {
    nodeType: 'generate_voice',
    label: '生成音频',
    category: PRODUCTION,
    family: 'audio_generator',
    iconKey: 'mic',
    icon: <AudioOutlined />,
    color: '#6B9FE8',
    description: '使用声音模板将文本合成为音频。',
    suggestedNext: ['voice'],
    defaultFields: {
      text: '',
      selectedVoiceTemplateId: null,
      selectedSystemVoiceKey: null,
      selectedVoiceLabel: null,
      connectedVoiceTemplateId: null,
      connectedVoiceLabel: null,
      status: 'READY',
      artifactId: null,
      audioAssetId: null,
      previousAudioAssetId: null,
      durationMs: null,
      errorMessage: null,
      errorCode: null,
      selectedVoiceTemplateExecutable: null,
      needsRegeneration: false,
      pendingVoiceTemplateCandidateNodeId: null,
      generationHistory: [],
      skillSelectionSource: 'AUTO',
      skillKey: null,
      skillVersion: null,
      providerSelectionMode: 'DEFAULT',
      providerOptionId: null,
    },
  },
  {
    nodeType: 'motion_reconstruction',
    label: '动作重建',
    category: PRODUCTION,
    family: 'motion_processor',
    // Was `<NodeIndexOutlined />` — shared with `motion_asset` (spec §31
    // requires Motion Reconstruction be distinguishable).
    iconKey: 'motion-reconstruction',
    icon: <DeploymentUnitOutlined />,
    color: '#5EC9A6',
    description: '从视频输入重建为可预览的 3D 骨架动作。',
    suggestedNext: ['generate_video', 'motion_asset'],
    defaultFields: {
      qualityMode: 'balanced',
      prompt: '',
      status: 'WAITING',
      reconstructionId: null,
      jobId: null,
      inputVideoAssetId: null,
      inputVideoLabel: null,
      outputMotionAssetId: null,
      previousOutputMotionAssetId: null,
      needsRegeneration: false,
      errorCode: null,
      errorMessage: null,
    },
  },
  {
    nodeType: 'generate_keyframe',
    label: '生成关键帧',
    category: PRODUCTION,
    family: 'action',
    iconKey: 'lightning',
    icon: <ThunderboltOutlined />,
    color: '#F0637C',
    description: '调用关键帧生成能力。',
    suggestedNext: ['keyframe'],
  },
  {
    nodeType: 'generate_video',
    label: '生成视频',
    category: PRODUCTION,
    family: 'video_generator',
    iconKey: 'video-generation',
    icon: <PlaySquareOutlined />,
    color: '#F0637C',
    description: '调用视频生成能力，产出一段视频素材。',
    suggestedNext: ['video_asset'],
    defaultFields: {
      promptText: '',
      ratio: '16:9',
      qualityTier: '1080P',
      engine: 'auto',
      status: 'READY',
    },
  },
  {
    nodeType: 'lip_sync',
    label: '对口型 / 音画合成',
    category: PRODUCTION,
    family: 'action',
    iconKey: 'lightning',
    icon: <ThunderboltOutlined />,
    color: '#F0637C',
    description: '将配音与画面对齐合成。',
    suggestedNext: ['video_asset'],
  },
  {
    nodeType: 'edit_composite',
    label: '剪辑 / 合成',
    category: PRODUCTION,
    family: 'action',
    iconKey: 'edit-composite',
    icon: <ScissorOutlined />,
    color: '#F0637C',
    description: '将多个场景/片段剪辑合成为一条完整内容。',
    suggestedNext: ['platform_adaptation', 'video_asset'],
  },

  // --------------------------------------------------------- INTELLIGENCE / AUTO
  {
    nodeType: 'analyze_content',
    label: '分析内容',
    category: INTELLIGENCE,
    family: 'action',
    iconKey: 'media-analysis',
    icon: <ExperimentOutlined />,
    color: '#B98CE0',
    description: '分析参考/竞品内容，提炼可复用的结论。',
    suggestedNext: ['generate_production_plan'],
  },
  {
    nodeType: 'generate_production_plan',
    label: '生成制作计划',
    category: INTELLIGENCE,
    family: 'action',
    iconKey: 'ai-deep-analysis',
    icon: <BulbOutlined />,
    color: '#B98CE0',
    description: '基于虚拟 IP 与参考内容生成整体制作计划。',
    // Scene container is no longer tenant-creatable (visual grouping only).
    suggestedNext: [],
  },
  {
    nodeType: 'check_optimize',
    label: '检查 / 优化',
    category: INTELLIGENCE,
    family: 'action',
    iconKey: 'check-optimize',
    icon: <CheckCircleOutlined />,
    color: '#B98CE0',
    description: '检查当前工作流并给出优化建议。',
    suggestedNext: [],
  },
  {
    nodeType: 'auto_complete_missing_assets',
    label: '自动补全素材',
    category: INTELLIGENCE,
    family: 'action',
    iconKey: 'auto-complete',
    icon: <RobotOutlined />,
    color: '#B98CE0',
    description: '自动识别并补全场景中缺失的素材（占位，尚未接入真实执行）。',
    suggestedNext: [],
  },

  // -------------------------------------------------------------------- OUTPUT
  {
    nodeType: 'production_artifact',
    label: '生产成品',
    category: OUTPUT,
    family: 'entity',
    iconKey: 'production-artifact',
    icon: <FileDoneOutlined />,
    color: '#5EC9A6',
    description: '一个正式产出物（脚本/分镜/成片等）。',
    suggestedNext: ['platform_adaptation', 'publish'],
  },
  {
    nodeType: 'platform_adaptation',
    label: '平台适配',
    category: OUTPUT,
    family: 'action',
    iconKey: 'platform-adaptation',
    icon: <AppstoreOutlined />,
    color: '#F0637C',
    description: '将成品适配为不同平台所需的版本。',
    suggestedNext: ['production_artifact'],
  },
  {
    nodeType: 'publish',
    label: '发布',
    category: OUTPUT,
    family: 'action',
    iconKey: 'publish',
    icon: <ExportOutlined />,
    color: '#F0637C',
    description: '发布到已连接的平台账号。',
    suggestedNext: ['performance'],
  },
  {
    nodeType: 'performance',
    label: '表现数据',
    category: OUTPUT,
    family: 'entity',
    iconKey: 'performance',
    icon: <BarChartOutlined />,
    color: '#5EC9A6',
    description: '一次发布后的表现数据快照。',
    suggestedNext: [],
  },
]

export const NODE_REGISTRY: Record<string, NodeDefinition> = Object.fromEntries(
  NODE_DEFINITIONS.map((def) => [def.nodeType, def]),
)

export function getNodeDefinition(nodeType: string): NodeDefinition {
  const base =
    NODE_REGISTRY[nodeType] ??
    ({
      nodeType,
      label: nodeType,
      category: 'content',
      family: 'entity',
      iconKey: FALLBACK_ICON_KEY,
      icon: resolveIconByKey(FALLBACK_ICON_KEY),
      color: '#94a3b8',
      description: '',
      suggestedNext: [],
    } as NodeDefinition)

  // Canonical assignment: Canvas Node Catalog icon_ref (session cache).
  // Fallback: legacy override map, then code registry default.
  const assigned = getAssignedIconRef(nodeType) || _iconOverrides[nodeType] || null
  if (!assigned) return base
  if (assigned === base.iconKey && !assigned.startsWith('custom:')) return base
  return {
    ...base,
    iconKey: assigned as NodeIconKey,
    icon: resolveCanvasNodeIcon(assigned),
  }
}

/**
 * @deprecated Occupancy must come from Platform Node Catalog (all rows).
 * Kept for older call sites; prefer `buildIconOccupancyFromCatalog`.
 */
export function listEffectiveIconAssignments(): Array<{
  nodeType: string
  label: string
  iconKey: string
}> {
  const fromAssign = Object.entries({
    ...Object.fromEntries(NODE_DEFINITIONS.map((d) => [d.nodeType, d.iconKey])),
    ..._iconOverrides,
  }).map(([nodeType, iconKey]) => ({
    nodeType,
    label: NODE_REGISTRY[nodeType]?.label || nodeType,
    iconKey,
  }))
  return fromAssign
}

export function nodesByCategory(): Array<{ category: CanvasNodeCategory; items: NodeDefinition[] }> {
  return CATEGORY_ORDER.map((category) => ({
    category,
    items: NODE_DEFINITIONS.filter((d) => d.category === category).map((d) => getNodeDefinition(d.nodeType)),
  }))
}
